import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { confirmLeave } from "@/components/LeaveGuard";
import { api } from "@/lib/api";
import { Search, Loader2 } from "lucide-react";

const GROUP = { rete: "Rete", clienti: "Clienti", offerte: "Offerte luce e gas", catalogo: "Catalogo", pratiche: "Pratiche luce, gas e Internet", spedizioni: "Spedizioni", ticket: "Ticket assistenza" };

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const box = useRef(null), input = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return undefined; }
    setBusy(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => api.get("/search", { params: { q: q.trim() }, signal: ctrl.signal }).then((r) => { setRes(r.data.results || []); setActive(0); }).catch(() => {}).finally(() => setBusy(false)), 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);
  useEffect(() => {
    const out = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); input.current?.focus(); setOpen(true); } };
    document.addEventListener("mousedown", out); document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", out); document.removeEventListener("keydown", key); };
  }, []);
  const go = (r) => { if (!r) return; setOpen(false); confirmLeave().then((ok) => { if (ok) { setQ(""); navigate(r.href); } }); };
  const onKey = (e) => {
    if (!res?.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, res.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); go(res[active]); }
    if (e.key === "Escape") setOpen(false);
  };
  let last = null;
  return <div ref={box} className="relative w-full max-w-2xl" data-testid="global-search">
    <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 focus-within:border-primary transition-colors">
      {busy ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : <Search className="w-4 h-4 text-muted-foreground" />}
      <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
        placeholder="Cerca dealer, clienti, offerte, prodotti… (Ctrl+K)" className="h-9 w-full bg-transparent text-sm outline-none" data-testid="global-search-input" aria-label="Cerca" />
    </div>
    {open && res && <div className="absolute z-50 mt-1 w-full max-h-[70vh] overflow-y-auto rounded-md border border-border bg-popover shadow-xl" data-testid="global-search-results">
      {res.length === 0 ? <p className="p-3 text-sm text-muted-foreground" data-testid="global-search-empty">Nessun risultato per «{q}».</p>
        : res.map((r, i) => { const head = r.type !== last; last = r.type; return <React.Fragment key={`${r.type}-${i}`}>
          {head && <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{GROUP[r.type] || r.type}</p>}
          <button type="button" onMouseEnter={() => setActive(i)} onClick={() => go(r)} className={`block w-full text-left px-3 py-2 text-sm transition-colors ${i === active ? "bg-secondary" : "hover:bg-secondary/60"}`} data-testid={`global-search-result-${i}`}>
            <span className="font-medium">{r.label}</span>{r.sub && <span className="block text-xs text-muted-foreground truncate">{r.sub}</span>}
          </button></React.Fragment>; })}
    </div>}
  </div>;
};
